import { logContact } from '../services/communicationLog';
import CallTalkingPoints from './CallTalkingPoints';
import React, { useEffect, useState } from 'react';
import { get, ref, update } from 'firebase/database';
import { database } from '../firebaseConfig';

// Shows on "Schedule Home Inspection" tasks: pick an inspector partner, then call or text in one tap.
// Text opens an editable default message, then Send opens it in the phone's messages app.
const defaultMessage = (firstName, address, client) => {
  const buyerName = [client?.firstName, client?.lastName].filter(Boolean).join(' ');
  const buyerContact = [client?.phone, client?.email].filter(Boolean).join(' / ');
  const buyer = buyerName ? ` for my buyer ${buyerName}${buyerContact ? ` (${buyerContact})` : ''}` : ' for one of my buyers';
  return `Hi ${firstName || 'there'}, this is Kelly Olin with RESIDENCE | eXp Realty. I'm scheduling a home inspection${address ? ` at ${address}` : ''}${buyer}. Please reach out to them directly to confirm which reports they want and to set up payment. What's your next available date and time? Call or text me at 239-488-5900.`;
};

export default function InspectorQuickContact({ task, dealAddress, client }) {
  const taskName = task.name || task.title || '';
  const address = dealAddress || task.dealAddress || '';
  const [inspectors, setInspectors] = useState([]);
  const [selectedId, setSelectedId] = useState(task.inspector?.partnerId || '');
  const [showText, setShowText] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!/schedule home inspection/i.test(taskName)) return;
    get(ref(database, 'partners')).then((snap) => {
      if (!snap.exists()) return;
      const list = Object.entries(snap.val())
        .map(([id, p]) => ({ id, ...p }))
        .filter((p) => p.partnerType === 'Inspector')
        .sort((a, b) => String(a.companyName || a.lastName || '').localeCompare(String(b.companyName || b.lastName || '')));
      setInspectors(list);
    }).catch((err) => console.error('Could not load inspectors', err));
  }, [taskName]);

  if (!/schedule home inspection/i.test(taskName)) return null;

  const chosen = inspectors.find((p) => p.id === selectedId) || null;
  const label = (p) => p.companyName || `${p.firstName || ''} ${p.lastName || ''}`.trim();
  const digits = (chosen?.phone || '').replace(/[^0-9+]/g, '');
  const contactFirst = chosen?.firstName || '';

  const pick = async (id) => {
    setSelectedId(id);
    setShowText(false);
    const p = inspectors.find((x) => x.id === id);
    if (!p || task.source !== 'transaction' || !task.transactionId) return;
    try {
      await update(ref(database, `transactions/${task.transactionId}/tasks/${task.id}`), {
        inspector: { partnerId: p.id, name: label(p), phone: p.phone || '', email: p.email || '' },
      });
    } catch (err) {
      console.error('Could not save inspector on task', err);
    }
  };

  const openText = () => {
    setMessage(defaultMessage(contactFirst, address, client));
    setShowText((v) => !v);
  };

  const btn = { display: 'inline-block', padding: '8px 14px', borderRadius: 6, fontWeight: 700, fontSize: 13, textDecoration: 'none', border: '1px solid #B8962E', background: '#B8962E', color: '#FFFFFF', cursor: 'pointer' };
  const btnOff = { ...btn, background: '#FFFFFF', color: '#B8962E', borderColor: '#D5D9E0', pointerEvents: 'none', opacity: 0.5 };

  return (
    <div style={{ margin: '6px 0' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
        <select value={selectedId} onChange={(e) => pick(e.target.value)} style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid #D5D9E0', fontSize: 13, minWidth: 200 }}>
          <option value="">Choose an inspector</option>
          {inspectors.map((p) => <option key={p.id} value={p.id}>{label(p)}</option>)}
        </select>
        <a href={digits ? `tel:${digits}` : undefined} style={digits ? btn : btnOff} onClick={() => logContact(task.transactionId, { kind: 'call', toPhone: digits, role: 'Inspector', taskName: task.name || task.title || '' })}>Call</a>
        <CallTalkingPoints task={task} />
        <button type="button" onClick={openText} disabled={!digits} style={digits ? btn : btnOff}>Text</button>
        {chosen?.phone && <span style={{ fontSize: 13, color: '#5B6270' }}>{chosen.phone}</span>}
        {inspectors.length === 0 && <span style={{ fontSize: 12, color: '#5B6270' }}>No inspectors saved yet. Add them under Partners.</span>}
      </div>
      {showText && digits && (
        <div style={{ marginTop: 8, padding: 10, border: '1px solid #D5D9E0', borderRadius: 8, background: '#FFFFFF' }}>
          <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={4} style={{ width: '100%', boxSizing: 'border-box', padding: 8, borderRadius: 6, border: '1px solid #D5D9E0', fontSize: 14, fontFamily: 'inherit', color: '#1A1A1A' }} />
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <a href={`sms:${digits}?body=${encodeURIComponent(message)}`} style={btn}>Send text</a>
            <button type="button" onClick={() => setShowText(false)} style={btnOff.opacity ? { ...btn, background: '#FFFFFF', color: '#5B6270', borderColor: '#D5D9E0', pointerEvents: 'auto', opacity: 1 } : btn}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
